import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import IProcessed_Report from "@models/IProcessed_Report";
import IOnGetPromoCodeUsageReportDTO from "@models/DTO/IOnGetPromoCodeUsageReportDTO";
import IPromoCodeUsageSummaryRow from "@models/IPromoCodeUsageSummaryRow";
import IOwTableCols from "@models/IOwTableCols";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import OwButton from "../../../shared/OwButton/OwButton";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import TitleTagEnum from "../../../../models/TitleTagEnum";
import ReportPeriod from "../ReportPeriod/ReportPeriod";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import "./PromoCodeUsageReport.scss";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    processedReport: IProcessed_Report;
};

// Humanize a raw column key for display, e.g. "Promo_Discount" -> "Promo Discount".
const humanizeColumn = (key: string): string => {
    return key
        .split("_")
        .map((word) => (word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(" ");
};

const MONEY_COLUMNS = new Set(["Subtotal", "Promo_Discount", "Grand_Total"]);

const formatMoney = (value: any): string => {
    return (Number(value) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const formatCell = (colName: string, value: any): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (MONEY_COLUMNS.has(colName)) {
        return formatMoney(value);
    }

    if (typeof value === "number") {
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return String(value);
};

/**
 * Promo Code Usage report: one row per order that carried a promo code in the period, plus a
 * per-code summary strip (order count, subtotal, promo discount). Excel comes from the API via the
 * standard processed-report download flow.
 */
export const PromoCodeUsageReport = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const intl = useIntl();

    const reportData = props.processedReport.report_data as IOnGetPromoCodeUsageReportDTO;

    const [downloadingExcel, setDownloadingExcel] = useState(false);

    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank", "noopener,noreferrer");
        }
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const rows = reportData?.rows || [];
    const summary: IPromoCodeUsageSummaryRow[] = reportData?.summary || [];

    const totals = useMemo(() => {
        return summary.reduce(
            (acc, row) => {
                acc.order_count += Number(row.order_count) || 0;
                acc.subtotal_total += Number(row.subtotal_total) || 0;
                acc.promo_discount_total += Number(row.promo_discount_total) || 0;
                return acc;
            },
            { order_count: 0, subtotal_total: 0, promo_discount_total: 0 }
        );
    }, [summary]);

    const columns: IOwTableCols[] = useMemo(() => {
        return (reportData?.columns || []).map((colName) => ({
            title: humanizeColumn(colName),
            valueProp: colName,
            skipTitleTranslate: true,
            renderFnEmitsRow: true,
            renderFn: (row: any) => formatCell(colName, row[colName]),
        }));
    }, [reportData?.columns]);

    const summaryColumns: IOwTableCols[] = [
        { title: "promo_code", valueProp: "promo_code" },
        {
            title: "order_count",
            valueProp: "order_count",
            renderFnEmitsRow: true,
            renderFn: (row: IPromoCodeUsageSummaryRow) => formatCell("order_count", row.order_count),
        },
        {
            title: "subtotal",
            valueProp: "subtotal_total",
            renderFnEmitsRow: true,
            renderFn: (row: IPromoCodeUsageSummaryRow) => formatMoney(row.subtotal_total),
        },
        {
            title: "promo_discount",
            valueProp: "promo_discount_total",
            renderFnEmitsRow: true,
            renderFn: (row: IPromoCodeUsageSummaryRow) => formatMoney(row.promo_discount_total),
        },
    ];

    return (
        <div className="PromoCodeUsageReport">
            <ReportPeriod className={"mb-3"} startDate={reportData?.periodStart} endDate={reportData?.periodEnd} />
            {/* Header with Excel download */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <OwButton
                        className="btn-outline-primary me-3"
                        btnSizeClass="btn-sm"
                        title={downloadingExcel ? "generating_excel" : "excel"}
                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                        fn={handleDownloadExcel}
                        disabled={downloadingExcel}
                        testId="promo-code-usage-excel"
                    />
                </div>
                <div className="d-flex gap-4">
                    <span>
                        <strong>{intl.formatMessage({ id: "total_rows" })}:</strong> {rows.length}
                    </span>
                    <span>
                        <strong>{intl.formatMessage({ id: "total_discount" })}:</strong> {formatMoney(totals.promo_discount_total)}
                    </span>
                </div>
            </div>

            {summary.length ? (
                <div className="summary-strip mb-4">
                    <OwTitle title={"promo_code_usage_summary"} tag={TitleTagEnum.H5} />
                    <OwFnTable
                        columns={summaryColumns}
                        dataSet={summary}
                        dataFetch={false}
                        initialData={false}
                        tableConfig={{ tableStriped: true, tableSmall: true }}
                    />
                    <div className="summary-totals d-flex gap-4 justify-content-end small">
                        <span>
                            <strong>{intl.formatMessage({ id: "order_count" })}:</strong> {totals.order_count}
                        </span>
                        <span>
                            <strong>{intl.formatMessage({ id: "subtotal" })}:</strong> {formatMoney(totals.subtotal_total)}
                        </span>
                        <span>
                            <strong>{intl.formatMessage({ id: "promo_discount" })}:</strong> {formatMoney(totals.promo_discount_total)}
                        </span>
                    </div>
                </div>
            ) : null}

            <OwFnTable
                columns={columns}
                dataSet={rows}
                dataFetch={false}
                initialData={false}
                tableConfig={{ tableStriped: true, tableHover: true, tableSmall: true }}
            />
        </div>
    );
};
